import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  forgotPasswordRequest,
  resendForgotOtpRequest,
  resetPasswordRequest,
  verifyForgotOtpRequest,
  type ForgotPasswordPayload,
  type ResetPasswordPayload,
  type VerifyForgotOtpPayload,
} from '@/features/auth/api/authApi';
import { getApiMessage } from '@/features/auth/api/session';
import { getErrorMessage } from '@/shared/api/errors';

export function useForgotPasswordMutation() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: ForgotPasswordPayload) =>
      forgotPasswordRequest(payload),
    onSuccess: (data, payload) => {
      localStorage.setItem('user_mobile', payload.identifier || '');
      toast.success(getApiMessage(data, 'OTP sent successfully'));
      navigate('/otp-verify', {
        state: {
          type: 'Forgot',
          email: payload.identifier || null,
        },
      });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to send OTP'));
    },
  });
}

export function useResendForgotOtpMutation() {
  return useMutation({
    mutationFn: (payload: ForgotPasswordPayload) =>
      resendForgotOtpRequest(payload),
    onSuccess: (data) => {
      toast.success(getApiMessage(data, 'OTP resent successfully'));
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to resend OTP'));
    },
  });
}

export function useVerifyForgotOtpMutation() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: VerifyForgotOtpPayload) =>
      verifyForgotOtpRequest(payload),
    onSuccess: (data) => {
      toast.success(data?.message || 'OTP verified');
      navigate('/change-password', {
        state: {
          resetToken: data?.data?.resetToken,
          email: data?.data?.identifier,
        },
      });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'OTP verification failed'));
    },
  });
}

export function useResetPasswordMutation() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: ResetPasswordPayload) =>
      resetPasswordRequest(payload),
    onSuccess: (data) => {
      toast.success(getApiMessage(data, 'Password reset successfully'));
      navigate('/login', { replace: true });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to reset password'));
    },
  });
}
